import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, MapPin } from "lucide-react";
import MiniLocationMap from "@/components/app/MiniLocationMap";
import PrecisionNote from "@/components/app/PrecisionNote";

const CENTER = { lat: -22.2139, lng: -49.9456 };

export default function LocationSearch({ query, value, onChange, hint = "Confira se o pin está no lugar certo.", testid = "location" }) {
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const atCenter = value && Math.abs(value.lat - CENTER.lat) < 1e-4 && Math.abs(value.lng - CENTER.lng) < 1e-4;
  const last = useRef(value && !atCenter ? (query || "").trim() : "");
  const [found, setFound] = useState(value && !atCenter ? value.label || null : null);
  const reqId = useRef(0);
  const pick = (r) => { setFound(r.label); onChange({ lat: r.lat, lng: r.lng, label: r.label, precision: r.precision }); };

  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 6 || q === last.current) return undefined;
    const t = setTimeout(async () => {
      last.current = q;
      const id = ++reqId.current;
      setBusy(true);
      try {
        const { data } = await api.get("/geocode", { params: { q } });
        if (id !== reqId.current) return;
        setResults(data);
        if (data.length) {
          const best = data[0];
          console.info("[endereço] localizado", q, "→", best.label, best.lat, best.lng, best.precision);
          pick(best);
        } else {
          console.warn("[endereço] não encontrado", q);
          setFound(null);
        }
      } catch { if (id === reqId.current) setResults([]); } finally { if (id === reqId.current) { setBusy(false); setSearched(true); } }
    }, 1100);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line

  return (
    <div className="space-y-2" data-testid={`${testid}-search`}>
      {busy && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando endereço...</div>}
      {results.length > 1 && (
        <div className="space-y-1.5">
          {results.slice(0, 4).map((r, i) => (
            <button key={i} type="button" onClick={() => pick(r)}
              className={`w-full text-left rounded-lg border p-2.5 text-xs press ${value?.lat === r.lat && value?.lng === r.lng ? "border-primary bg-primary/10" : "border-white/10 bg-black/30"}`}
              data-testid={`${testid}-result-${i}`}>
              <div className="font-medium">{r.label}</div>
            </button>
          ))}
        </div>
      )}
      {searched && !busy && results.length === 0 && (
        <p className="text-xs text-destructive" data-testid={`${testid}-not-found`}>Endereço não encontrado em Marília. Confira o nome da rua e o número, ou marque o local no mapa.</p>
      )}
      {!value && searched && results.length === 0 && (
        <button type="button" onClick={() => onChange({ ...CENTER, manual: true })} className="text-xs text-primary flex items-center gap-1 press" data-testid={`${testid}-manual`}>
          <MapPin className="w-3.5 h-3.5" /> Marcar o local tocando no mapa
        </button>
      )}
      {value && (
        <>
          <MiniLocationMap lat={value.lat} lng={value.lng} zoom={value.manual && !value.moved ? 14 : 17}
            onChange={({ lat, lng }) => onChange({ ...value, lat, lng, manual: true, moved: true })} hint="Arraste" />
          {found && !value.moved ? (
            <>
              <p className="text-[11px] text-emerald-400 font-medium" data-testid={`${testid}-found`}>Localizado automaticamente: {found}</p>
              <PrecisionNote precision={value.precision} testid={`${testid}-precision`} />
            </>
          ) : <p className="text-[11px] text-primary font-medium" data-testid={`${testid}-hint`}>{value.moved ? "Pin ajustado manualmente." : hint}</p>}
        </>
      )}
    </div>
  );
}
